import { __, sprintf } from '@wordpress/i18n';
import { useState, useEffect } from '@wordpress/element';
import { Button, Spinner, Notice } from '@wordpress/components';
import { ImageModal } from './';

const DuplicatesTab = ({ refreshStats }) => {
    const [groups, setGroups] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState({});
    const [notice, setNotice] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);

    // Pagination state
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const limit = 10;

    useEffect(() => {
        fetchDuplicates();
    }, [page]);

    const fetchDuplicates = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(window.mtrimAdminData.ajaxUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    action: 'mtrim_get_duplicates',
                    nonce: window.mtrimAdminData.nonce,
                    page: page,
                    limit: limit
                })
            });

            const data = await response.json();
            if (data.success) {
                setGroups(data.data.items);
                setTotalPages(data.data.pages);
                setTotalItems(data.data.total);
            }
        } catch (error) {
            console.error('Error fetching duplicates:', error);
        }
        setIsLoading(false);
    };

    const handleKeep = async (attachmentId, hash) => {
        if (!confirm(__('Keep this image and move other duplicates in this group to trash?', 'media-trim'))) {
            return;
        }

        setActionLoading({ ...actionLoading, [attachmentId]: true });
        try {
            const response = await fetch(window.mtrimAdminData.ajaxUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'mtrim_keep_duplicate',
                    nonce: window.mtrimAdminData.nonce,
                    attachment_id: attachmentId,
                    file_hash: hash,
                }),
            });
            const data = await response.json();
            if (data.success) {
                setNotice({ type: 'success', message: data.data.message });
                refreshStats();
                
                // If we cleared a group on the last page, check if we need to go back
                if (groups.length === 1 && page > 1) {
                    setPage(page - 1);
                } else {
                    fetchDuplicates();
                }
            } else {
                setNotice({ type: 'error', message: data.data.message });
            }
        } catch (error) {
            setNotice({ type: 'error', message: __('Failed to process duplicate', 'media-trim') });
        }
        setActionLoading({ ...actionLoading, [attachmentId]: false });
    };

    const handleAutoMerge = async () => {
        if (!confirm(__('Automatically merge all duplicate groups? This will keep the oldest version of each image.', 'media-trim'))) {
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(window.mtrimAdminData.ajaxUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'mtrim_auto_merge_duplicates',
                    nonce: window.mtrimAdminData.nonce,
                }),
            });
            const data = await response.json();
            if (data.success) {
                setGroups([]);
                setPage(1);
                setNotice({ type: 'success', message: data.data.message });
                refreshStats();
            } else {
                setNotice({ type: 'error', message: data.data.message });
            }
        } catch (error) {
            setNotice({ type: 'error', message: __('Failed to auto-merge duplicates', 'media-trim') });
        }
        setIsLoading(false);
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pages = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            if (page <= 4) {
                pages.push(1, 2, 3, 4, 5, '...', totalPages);
            } else if (page >= totalPages - 3) {
                pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
            } else {
                pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
            }
        }

        return (
            <div className="mtrim-pagination-wrapper">
                <div className="mtrim-pagination">
                    <button 
                        className="mtrim-pagination-btn"
                        disabled={page === 1} 
                        onClick={() => setPage(page - 1)}
                        title={__('Previous Page', 'media-trim')}
                    >
                        <span className="dashicons dashicons-arrow-left-alt2"></span>
                    </button>
                    
                    {pages.map((p, index) => (
                        p === '...' ? (
                            <span key={`ellipsis-${index}`} className="mtrim-pagination-btn is-ellipsis">...</span>
                        ) : (
                            <button 
                                key={p}
                                className={`mtrim-pagination-btn ${page === p ? 'is-active' : ''}`}
                                onClick={() => setPage(p)}
                            >
                                {p}
                            </button>
                        )
                    ))}

                    <button 
                        className="mtrim-pagination-btn"
                        disabled={page === totalPages} 
                        onClick={() => setPage(page + 1)}
                        title={__('Next Page', 'media-trim')}
                    >
                        <span className="dashicons dashicons-arrow-right-alt2"></span>
                    </button>
                </div>
                <div className="mtrim-page-info">
                    {sprintf(__('Page %d of %d', 'media-trim'), page, totalPages)}
                    <span className="mtrim-separator">|</span>
                    {sprintf(__('Total %d groups', 'media-trim'), totalItems)}
                </div>
            </div>
        );
    };

    return (
        <div className="mtrim-results-tab">
            <div className="mtrim-results-header">
                <h2>{__('Duplicate Media', 'media-trim')} <span>({totalItems})</span></h2>
                <div className="mtrim-results-actions">
                    <Button 
                        isSecondary
                        onClick={fetchDuplicates} 
                        disabled={isLoading}
                        icon="update"
                        label={__('Refresh', 'media-trim')}
                    />
                </div>
            </div>

            <div className="mtrim-filter-area">
                <div className="mtrim-filter-left">
                    <div className="mtrim-filter-group">
                        <Button 
                            isPrimary
                            onClick={handleAutoMerge}
                            disabled={groups.length === 0 || isLoading}
                        >
                            {__('Auto-Merge All', 'media-trim')}
                        </Button>
                    </div>
                </div>
            </div>

            {notice && (
                <Notice 
                    status={notice.type} 
                    isDismissible={true}
                    onRemove={() => setNotice(null)}
                    className="mtrim-notice-animate"
                >
                    {notice.message}
                </Notice>
            )}

            {isLoading && groups.length === 0 ? (
                <div className="mtrim-loading-state">
                    <Spinner />
                    <p>{__('Scanning for duplicates...', 'media-trim')}</p>
                </div>
            ) : groups.length === 0 ? (
                <div className="mtrim-empty-state">
                    <span className="dashicons dashicons-images-alt"></span>
                    <p>{__('No duplicates found. Your media library is clean!', 'media-trim')}</p>
                </div>
            ) : (
                <div className="mtrim-duplicates-list">
                    {groups.map((group, index) => (
                        <div key={group.hash} className="mtrim-duplicate-group">
                            <div className="mtrim-group-header">
                                <h3>{__('Group', 'media-trim')} #{ ( (page - 1) * limit ) + index + 1} <span>({group.items.length} {__('files', 'media-trim')})</span></h3>
                            </div>
                            <table className="mtrim-results-table">
                                <thead>
                                    <tr>
                                        <th>{__('Preview', 'media-trim')}</th>
                                        <th>{__('File Details', 'media-trim')}</th>
                                        <th>{__('Size', 'media-trim')}</th>
                                        <th>{__('Actions', 'media-trim')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {group.items.map(item => (
                                        <tr key={item.attachment_id}>
                                            <td style={{ width: '60px' }}>
                                                <div 
                                                    className="mtrim-media-preview"
                                                    onClick={() => setSelectedImage(item.url)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    {item.url ? <img src={item.url} alt="" /> : <span className="dashicons dashicons-media-default"></span>}
                                                </div>
                                            </td>
                                            <td className="mtrim-column-filename">
                                                <strong>{item.filename}</strong>
                                                <code>{item.file_path}</code>
                                                <span className="mtrim-item-id">ID: {item.attachment_id}</span>
                                            </td>
                                            <td>{item.formatted_size}</td>
                                            <td>
                                                <Button 
                                                    isSecondary
                                                    onClick={() => handleKeep(item.attachment_id, group.hash)}
                                                    disabled={actionLoading[item.attachment_id]}
                                                >
                                                    {actionLoading[item.attachment_id] ? <Spinner /> : __('Keep This', 'media-trim')}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                    {renderPagination()}
                </div>
            )}

            {selectedImage && (
                <ImageModal 
                    imageUrl={selectedImage} 
                    onClose={() => setSelectedImage(null)} 
                />
            )}
        </div>
    );
};

export default DuplicatesTab;
